import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Task, TaskFormData } from '../types';
import api from '../services/api';

// 初始状态类型
interface TaskState {
    tasks: Task[];
    loading: boolean;
    error: string | null;
}

// 初始状态
const initialState: TaskState = {
    tasks: [],
    loading: false,
    error: null
};

// 异步 thunk 动作
export const fetchTasks = createAsyncThunk(
    'tasks/fetchTasks',
    async () => {
        const response = await api.getTasks();
        // 处理不同的响应格式
        if (Array.isArray(response.data)) {
            return response.data;
        } else if (response.data && typeof response.data === 'object') {
            const data = response.data as { tasks?: Task[] };
            return Array.isArray(data.tasks) ? data.tasks : [];
        }
        return [];
    }
);

export const addTask = createAsyncThunk(
    'tasks/addTask',
    async (task: TaskFormData) => {
        const response = await api.createTask(task);
        return response.data;
    }
);

export const updateTask = createAsyncThunk(
    'tasks/updateTask',
    async ({ taskId, updatedTask }: { taskId: number; updatedTask: TaskFormData }) => {
        const response = await api.updateTask(taskId, updatedTask);
        return response.data;
    }
);

export const updateTaskStatus = createAsyncThunk(
    'tasks/updateTaskStatus',
    async ({ taskId, newStatus, isReset }: { taskId: number; newStatus: string; isReset: boolean }) => {
        const response = await api.updateTaskStatus(taskId, newStatus, isReset);
        return response.data;
    }
);

export const deleteTask = createAsyncThunk(
    'tasks/deleteTask',
    async (taskId: number) => {
        await api.deleteTask(taskId);
        return taskId;
    }
);

// 创建 slice
const taskSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // 处理 fetchTasks
        builder.addCase(fetchTasks.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTasks.fulfilled, (state, action) => {
            state.tasks = action.payload;
            state.loading = false;
        });
        builder.addCase(fetchTasks.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || '获取任务失败';
        });

        // 处理 addTask
        builder.addCase(addTask.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addTask.fulfilled, (state, action) => {
            state.tasks.push(action.payload);
            state.loading = false;
        });
        builder.addCase(addTask.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || '添加任务失败';
        });

        // 处理 updateTask
        builder.addCase(updateTask.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateTask.fulfilled, (state, action) => {
            const index = state.tasks.findIndex(task => task.id === action.payload.id);
            if (index !== -1) {
                state.tasks[index] = action.payload;
            }
            state.loading = false;
        });
        builder.addCase(updateTask.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || '更新任务失败';
        });

        // 处理 updateTaskStatus
        builder.addCase(updateTaskStatus.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateTaskStatus.fulfilled, (state, action) => {
            const index = state.tasks.findIndex(task => task.id === action.payload.id);
            if (index !== -1) {
                state.tasks[index] = action.payload;
            }
            state.loading = false;
        });
        builder.addCase(updateTaskStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || '更新任务状态失败';
        });

        // 处理 deleteTask
        builder.addCase(deleteTask.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteTask.fulfilled, (state, action) => {
            state.tasks = state.tasks.filter(task => task.id !== action.payload);
            state.loading = false;
        });
        builder.addCase(deleteTask.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || '删除任务失败';
        });
    },
});

// Selectors
export const selectAllTasks = (state: { tasks: TaskState }) => state.tasks.tasks;
export const selectTodoTasks = (state: { tasks: TaskState }) =>
    state.tasks.tasks.filter(task => task.status === '日常');
export const selectInProgressTasks = (state: { tasks: TaskState }) =>
    state.tasks.tasks.filter(task => task.status === '工作');
export const selectPendingCommunicationTasks = (state: { tasks: TaskState }) =>
    state.tasks.tasks.filter(task => task.status === '搁置');
export const selectCompletedTasks = (state: { tasks: TaskState }) =>
    state.tasks.tasks.filter(task => task.status === '已完成');
export const selectLoading = (state: { tasks: TaskState }) => state.tasks.loading;
export const selectError = (state: { tasks: TaskState }) => state.tasks.error;

export default taskSlice.reducer;